import { type ImageJob, operationSpec } from '@acasa/shared'
import { useState } from 'react'
import { JobCard } from './job-card'
import { STATUS_LABELS } from './operations'

/**
 * Every generation ever made, newest first.
 *
 * Rows are COLLAPSED and carry the SOURCE thumbnail, not the result. onOffice's
 * CDN resizes on demand, so a source thumbnail is a few kB; a result is the
 * full generated file, and this API does not resize — a hundred rows of those
 * would be a hundred megabytes to render a list. The real image loads when a
 * row is opened.
 */
function dayLabel(iso: string) {
  const startOfDay = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

  const date = new Date(iso)
  const days = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86_400_000)

  if (days === 0) return 'Heute'
  if (days === 1) return 'Gestern'
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/**
 * The source thumbnail, degrading to a plain tile.
 *
 * A history outlives its sources: an image deleted in onOffice leaves a url
 * that 404s, and the browser's broken-image icon in a tidy list looks like the
 * app is broken rather than the picture being gone.
 */
function SourceThumb({ url, alt }: { url: string; alt: string }) {
  const [failed, setFailed] = useState(false)

  if (failed) {
    return (
      <span
        aria-hidden
        title="Quellbild in onOffice nicht mehr vorhanden"
        className="bg-raised border-line text-faint flex h-9 w-12 shrink-0 items-center justify-center border text-[9px]"
      >
        ?
      </span>
    )
  }

  return (
    <img
      // The picture that went in — enough to recognise the job, and a few kB
      // because onOffice's CDN sizes it for us.
      src={`${url}@96x72`}
      alt={alt}
      loading="lazy"
      referrerPolicy="no-referrer"
      draggable={false}
      onError={() => setFailed(true)}
      className="no-drag bg-raised h-9 w-12 shrink-0 object-cover"
    />
  )
}

const DOT: Record<string, string> = {
  succeeded: 'bg-done',
  failed: 'bg-danger',
  running: 'bg-brand',
  queued: 'bg-line-strong',
}

export function GenerationHistory({
  jobs,
  estateLabels,
  onRemove,
}: {
  jobs: ImageJob[]
  /** estateId → ImmoNr, so a row says which object it belongs to. */
  estateLabels: Map<number, string>
  onRemove: (id: string) => void
}) {
  const [openId, setOpenId] = useState<string | null>(null)

  if (jobs.length === 0) {
    return (
      <div className="border-line text-faint border border-dashed px-4 py-10 text-center text-xs">
        Noch nichts generiert.
      </div>
    )
  }

  let lastDay: string | null = null

  return (
    <div className="flex flex-col">
      {jobs.map((job) => {
        const spec = operationSpec(job.operation)
        const day = dayLabel(job.createdAt)
        const newDay = day !== lastDay
        lastDay = day
        const isOpen = openId === job.id

        return (
          <div key={job.id}>
            {newDay && (
              <p className="text-faint mt-4 mb-1.5 text-[10px] tracking-wider uppercase first:mt-0">
                {day}
              </p>
            )}

            <div className="bg-surface border-line mb-1.5 border">
              <button
                type="button"
                onClick={() => setOpenId(isOpen ? null : job.id)}
                aria-expanded={isOpen}
                className="hover:bg-canvas flex w-full items-center gap-3 px-2.5 py-2 text-left transition-colors"
              >
                <SourceThumb url={job.sourceUrl} alt="" />

                <span className="min-w-0 flex-1">
                  <span className="text-ink block truncate text-xs">{spec.label}</span>
                  <span className="text-faint block truncate text-[11px]">
                    {estateLabels.get(job.estateId) ?? `Objekt ${job.estateId}`}
                    {job.style && ` · ${job.style}`}
                  </span>
                </span>

                <span className="text-faint shrink-0 text-right text-[10px]">
                  <span className="tnum block font-mono">
                    {new Date(job.createdAt).toLocaleTimeString('de-DE', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  <span className="mt-0.5 flex items-center justify-end gap-1">
                    <span aria-hidden className={`size-1.5 rounded-full ${DOT[job.status]}`} />
                    {STATUS_LABELS[job.status]}
                  </span>
                </span>
              </button>

              {isOpen && (
                <div className="border-line border-t">
                  <JobCard
                    job={job}
                    onRemove={(id) => {
                      setOpenId(null)
                      onRemove(id)
                    }}
                    estateLabel={estateLabels.get(job.estateId)}
                  />
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
